import React, { useCallback, useEffect, useState } from 'react';
import Modal from 'view/components/common/Modal';
import { faPlusSquare, faSave } from '@fortawesome/free-solid-svg-icons';
import AssignmentFeedback, {
  AssignmentFeedbackType,
} from 'models/assignments/AssignmentFeedback';
import useApiCall from 'contexts/ApiCall';
import teacherService from 'services/teacherService';
import ActionButton from 'view/components/buttons/ActionButton';

interface SelectionRange {
  text: string;
  startIndex: number;
  endIndex: number;
}

interface AnnotationModalProps {
  submissionId: string;
  questionId?: string;
  isOpen: boolean;
  originalFeedback?: AssignmentFeedback;
  onClose: () => void;
  selection: SelectionRange | null;
  onSubmit?: (feedback: AssignmentFeedback) => void;
}

const AnnotationModal: React.FC<AnnotationModalProps> = ({
  submissionId,
  questionId,
  isOpen,
  originalFeedback,
  selection,
  onClose,
  onSubmit = () => {},
}) => {
  const [feedback, setFeedback] = useState<AssignmentFeedback>();
  const makeApiCall = useApiCall();

  useEffect(() => {
    if (originalFeedback) {
      setFeedback(originalFeedback);
      return;
    }

    if (!selection) {
      setFeedback(AssignmentFeedback.generateDefault(submissionId));
    } else {
      setFeedback(
        AssignmentFeedback.generateDefault(
          submissionId,
          questionId,
          selection.startIndex,
          selection.endIndex,
        ),
      );
    }
  }, [selection, submissionId, questionId, originalFeedback]);

  const updateFeedback = (event: any) => {
    setFeedback((prev) =>
      AssignmentFeedback.fromServerFeedback({
        ...(prev?.toJSON() ?? {}),
        [event.target.id]: event.target.value,
      }),
    );
  };

  const handleCreateFeedback = () => {
    makeApiCall(teacherService.createAssignmentFeedback, feedback)
      .then((resp) => {
        onSubmit(resp);
        setFeedback(AssignmentFeedback.generateDefault(submissionId));
        onClose();
      })
      .catch((error) => alert('Failed to create feedback! ' + error));
  };

  const handleUpdateFeedback = useCallback(() => {
    makeApiCall(teacherService.updateAssignmentFeedback, feedback)
      .then((resp) => {
        onSubmit(resp);
        onClose();
      })
      .catch((error) => alert('Failed to update feedback! ' + error));
  }, [feedback, makeApiCall, onClose, onSubmit]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="form" style={{ textAlign: 'left' }}>
        <label className="label-large">Add comment</label>

        {selection && (
          <div className="assignment-attribute-container">
            <div className="sidebar-text">
              <strong className="label-normal">Selected text:</strong>{' '}
              {selection.text}
            </div>
          </div>
        )}

        <div className="assignment-attribute-container">
          <div className="sidebar-text">
            <label className="label-normal">Correction type:</label>
          </div>
          <select
            id="correction_type"
            value={feedback?.correction_type}
            onChange={updateFeedback}
          >
            {Object.values(AssignmentFeedbackType).map((type) => (
              <option key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div className="assignment-attribute-container">
          <div className="sidebar-text">
            <label className="label-normal">Comment:</label>
          </div>
          <textarea
            id="text"
            rows={3}
            value={feedback?.text}
            onChange={updateFeedback}
          />
        </div>

        <ActionButton
          type="go"
          onClick={
            originalFeedback ? handleUpdateFeedback : handleCreateFeedback
          }
          label={originalFeedback ? 'Update comment' : 'Add comment'}
          icon={originalFeedback ? faSave : faPlusSquare}
        />
      </div>
    </Modal>
  );
};

export default AnnotationModal;
